import './FilterBar.css';
import React from 'react';
import { camelCase } from 'lodash';
import { Form } from '../Form/Form';
import { SearchSelect } from '../Form/Select/SearchSelect';
import { Datepicker } from '../Form/Datepicker/Datepicker';
import { useQueryAsState } from '../../model/history';
import { useRemoteData } from '../../model/data';
import { getStorageLocations } from '../../model/storageLocation';
import { ListSearch } from '../ListSearch/ListSearch';
import { t } from '../../locales/translate';
import { Checkbox } from '../Form/Checkbox/Checkbox';
import { useErrorHandler } from '../../util/util';
import { MostUsedFilters } from '../../model/state';
import { Grid } from '../Grid/Grid';
import { paymentMethods } from '../../model/payment';

type allowedFilters =
  | 'query'
  | 'timeframe'
  | 'status'
  | 'deliveryStatus'
  | 'storageLocation'
  | 'withShippableProductsOnly'
  | 'paymentMethod'
  | 'archived'
  | 'includeArchived';

interface Props {
  filters: allowedFilters[];
  filterOrder?: Partial<Record<allowedFilters, number>>;
  filterData?: {
    statusData?: string[];
    sortByFrequencyId?: keyof MostUsedFilters;
    paymentMethodData?: string[];
  };
  extraFilters?: Array<{
    key: string;
    order?: number;
    renderer: (value: string | undefined, onChange: (value?: string) => void) => React.ReactNode;
  }>;
  disableDefaults?: boolean;
  queryStyle?: React.CSSProperties;
}

export function FilterBar({
  filters,
  filterOrder,
  filterData,
  extraFilters,
  disableDefaults,
  queryStyle,
}: Props): JSX.Element {
  const [params, setParams] = useQueryAsState();

  return (
    <Form
      className="filter-bar"
      model={
        disableDefaults
          ? params
          : {
              timeframe: params.timeframe,
              status: params.status || [],
              storageLocation: params.storageLocation || [],
              withShippableProductsOnly: params.withShippableProductsOnly === 'true' ? true : false,
              paymentMethod: params.paymentMethod || [],
              archived: params.archived || 'false',
              includeArchived: params.archived === 'true',
            }
      }
      onInput={(model) => {
        if (disableDefaults || !('includeArchived' in model)) {
          setParams({ ...model, withShippableProductsOnly: params.withShippableProductsOnly });
          return;
        }
        // includeArchived is only a checkbox proxy for the `archived` param and must not end up in the URL itself
        const { includeArchived, ...value } = model;
        setParams({
          ...value,
          timeframe: value.timeframe ?? undefined,
          withShippableProductsOnly: value.withShippableProductsOnly ? 'true' : 'false',
          ...(filters.includes('includeArchived') && { archived: includeArchived ? 'true' : 'false' }),
          page: 0,
        });
      }}
    >
      <Grid flex gap="sm">
        {filters.includes('query') && (
          <ListSearch
            query={params.query || []}
            onChange={(query) => setParams({ query, page: 0 })}
            style={{ order: filterOrder?.['query'], ...queryStyle }}
          />
        )}
        {filters.includes('timeframe') && (
          <Datepicker
            hourpicker
            rangeOfDays
            name="timeframe"
            label={t.model.datetime.timeframe}
            style={{ order: filterOrder?.['timeframe'] }}
          />
        )}
        {filters.includes('status') && (
          <SearchSelect
            name="status"
            label={t.model.status._name}
            icon="activity--sm"
            style={{ order: filterOrder?.['status'] }}
            sortByFrequencyId={filterData?.sortByFrequencyId}
            values={((filterData && filterData.statusData) || []).map((e) => ({
              id: e,
              label: t.model.status[camelCase(e)],
            }))}
          />
        )}
        {filters.includes('storageLocation') && <StorageLocationsSelect />}
        {filters.includes('paymentMethod') && (
          <SearchSelect
            name="paymentMethod"
            label={t.model.payment.paymentMethod}
            icon="money"
            values={paymentMethods.map((e) => ({
              id: e.id,
              label: e.name,
            }))}
          />
        )}
        {filters.includes('withShippableProductsOnly') && (
          <Checkbox
            name="withShippableProductsOnly"
            label={t.model.order.withShippableProductsOnly}
            style={{ order: filterOrder?.['withShippableProductsOnly'] }}
          ></Checkbox>
        )}
        {filters.includes('includeArchived') && (
          <Checkbox
            name="includeArchived"
            label={t.model.order.includeArchive}
            style={{ order: filterOrder?.['includeArchived'] }}
          ></Checkbox>
        )}
        {filters.includes('archived') && (
          <SearchSelect
            name="archived"
            label={t.general.showing}
            required={true}
            disableTagRemove={true}
            icon="eye-off--sm"
            style={{ order: filterOrder?.['archived'] }}
            values={[
              { id: 'true', label: t.general.archived },
              { id: 'false', label: t.general.notArchived },
              { id: 'all', label: t.general.all },
            ]}
          />
        )}
        {extraFilters?.map((item) => {
          return (
            <div key={item.key} style={{ order: item.order }}>
              {item.renderer(params[item.key], (value) => setParams({ ...params, [item.key]: value }))}
            </div>
          );
        })}
      </Grid>
    </Form>
  );
}

function StorageLocationsSelect(): JSX.Element | null {
  const [storageLocations, loading, error] = useRemoteData(
    getStorageLocations,
    (db) => db.table('storageLocations').all
  );

  useErrorHandler(error);

  if (loading || error) {
    return null;
  }

  return (
    <SearchSelect
      name="storageLocation"
      label={t.model.order.storageLocation}
      icon="home"
      values={storageLocations.map((location) => ({
        id: location.id,
        label: location.name,
      }))}
    />
  );
}
